import api from '@/services/api'

const MENU_TREE_CACHE_KEY = 'menu_tree_cache'

const state = {
  token: null,
  user: null,
  permissions: [],
  // 從 localStorage 還原，避免頁面重整時選單閃爍
  menuTree: JSON.parse(localStorage.getItem(MENU_TREE_CACHE_KEY) || '[]'),
}

const getters = {
  isAuthenticated: (state) => !!state.token,
  currentUser: (state) => state.user,
  permissions: (state) => state.permissions,
  hasPermission: (state) => (permission) => state.permissions.includes(permission),
  menuTree: (state) => state.menuTree,
  sidebarMenus: (state) => {
    const perms = state.permissions || []
    const filterMenu = (items) => {
      return items.reduce((acc, item) => {
        const filteredChildren = item.children ? filterMenu(item.children) : []
        const selfPermitted = perms.includes(item.code)
        const hasVisibleChildren = filteredChildren.length > 0

        if (selfPermitted || hasVisibleChildren) {
          acc.push({ ...item, children: filteredChildren })
        }
        return acc
      }, [])
    }
    return filterMenu(state.menuTree)
  },
}

const mutations = {
  SET_TOKEN(state, token) {
    state.token = token
    if (token) {
      localStorage.setItem('access_token', token)
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`
    } else {
      localStorage.removeItem('access_token')
      delete api.defaults.headers.common['Authorization']
    }
  },
  SET_USER(state, user) {
    state.user = user
  },
  SET_PERMISSIONS(state, permissions) {
    state.permissions = permissions
  },
  SET_MENU_TREE(state, menus) {
    state.menuTree = menus
    // 快取到 localStorage，下次載入頁面時立刻顯示選單
    try {
      localStorage.setItem(MENU_TREE_CACHE_KEY, JSON.stringify(menus))
    } catch (e) {
      // localStorage 空間不足時靜默忽略
    }
  },
  CLEAR_AUTH(state) {
    state.token = null
    state.user = null
    state.permissions = []
    state.menuTree = []
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem(MENU_TREE_CACHE_KEY)
    delete api.defaults.headers.common['Authorization']
  },
}

const actions = {
  async login({ commit, dispatch }, credentials) {
    try {
      const response = await api.post('/auth/login', credentials)
      const { access_token, refresh_token } = response.data
      
      commit('SET_TOKEN', access_token)
      localStorage.setItem('refresh_token', refresh_token)
      
      // Fetch user info and menu tree in parallel
      const [userResponse] = await Promise.all([
        api.get('/auth/me'),
        dispatch('fetchMenuTree'),
      ])
      commit('SET_USER', userResponse.data)
      commit('SET_PERMISSIONS', userResponse.data.permissions)
      
      return { success: true }
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.detail || '登入失敗' 
      }
    }
  },
  
  async googleLogin({ commit, dispatch }, credential) {
    try {
      const response = await api.post('/auth/google', { credential })
      const { access_token, refresh_token } = response.data
      
      commit('SET_TOKEN', access_token)
      localStorage.setItem('refresh_token', refresh_token)
      
      // Fetch user info and menu tree in parallel
      const [userResponse] = await Promise.all([
        api.get('/auth/me'),
        dispatch('fetchMenuTree'),
      ])
      commit('SET_USER', userResponse.data)
      commit('SET_PERMISSIONS', userResponse.data.permissions)
      
      return { success: true }
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Google 登入失敗' 
      }
    }
  },
  
  async fetchCurrentUser({ commit, dispatch, state }) {
    if (!state.token) return
    
    try {
      // Fetch user info and menu tree in parallel
      const [response] = await Promise.all([
        api.get('/auth/me'),
        dispatch('fetchMenuTree'),
      ])
      commit('SET_USER', response.data)
      commit('SET_PERMISSIONS', response.data.permissions)
    } catch (error) {
      commit('CLEAR_AUTH')
    }
  },

  async fetchMenuTree({ commit, state }) {
    if (!state.token) return
    try {
      const response = await api.get('/menus')
      commit('SET_MENU_TREE', response.data)
    } catch (error) {
      console.error('Failed to fetch menu tree:', error)
    }
  },
  
  async logout({ commit }) {
    try {
      await api.post('/auth/logout')
    } catch (error) {
      // Ignore logout errors
    }
    commit('CLEAR_AUTH')
  },
  
  async refreshToken({ commit }) {
    const refreshToken = localStorage.getItem('refresh_token')
    if (!refreshToken) {
      commit('CLEAR_AUTH')
      return false
    }
    
    try {
      const response = await api.post('/auth/refresh', { 
        refresh_token: refreshToken 
      })
      const { access_token, refresh_token: newRefreshToken } = response.data
      
      commit('SET_TOKEN', access_token)
      localStorage.setItem('refresh_token', newRefreshToken)
      
      return true
    } catch (error) {
      commit('CLEAR_AUTH')
      return false
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
